import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, addDoc, collection, increment } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';

const Donate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [amount, setAmount] = useState<string>('');
  const [referrerId, setReferrerId] = useState<string | null>(null);
  const [referrerName, setReferrerName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  
  // Parse URL params to get referrer info
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refId = params.get('referrerId');
    
    if (refId) {
      setReferrerId(refId);
      // Fetch referrer's name
      const fetchReferrerName = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', refId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setReferrerName(`${userData.firstName} ${userData.lastName}`);
          }
        } catch (error) {
          console.error('Error fetching referrer details:', error);
        }
      };
      
      fetchReferrerName();
    }
  }, [location]);
  
  const handleAmountClick = (selectedAmount: string) => {
    setAmount(selectedAmount);
  };
  
  const handleDonate = async () => {
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      alert('Please enter a valid donation amount');
      return;
    }
    
    setLoading(true);
    
    try {
      // Record the donation in Firestore first
      const donationData: {
        amount: number;
        createdAt: string;
        status: string;
        referrerId?: string;
        donorId?: string;
      } = {
        amount: Number(amount),
        createdAt: new Date().toISOString(),
        status: 'pending'
      };
      
      // If referred by someone, add referrer info
      if (referrerId) {
        donationData.referrerId = referrerId;
        
        // Update referrer's raised amount
        const userRef = doc(db, 'users', referrerId);
        await updateDoc(userRef, {
          totalRaised: increment(Number(amount))
        });
      }
      
      // Add donor info if logged in
      if (currentUser) {
        donationData.donorId = currentUser.uid;
      }
      
      // Save the donation record
      await addDoc(collection(db, 'donations'), donationData);
      
      // Redirect to Stripe
      const stripeUrl = `https://donate.stripe.com/28o6s80ikdjx3UQ144?amount=${encodeURIComponent(amount)}`;
      window.location.href = stripeUrl;
    } catch (error) {
      console.error('Error processing donation:', error);
      alert('There was an error processing your donation. Please try again.');
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-primary py-4 px-6">
            <h2 className="text-xl font-semibold text-white">Support Our Cause</h2>
          </div>
          
          <div className="p-6">
            <div className="mb-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Thank You For Your Generosity</h3>
              
              <p className="text-lg text-gray-700 mb-4">
                Thank you for your generosity. You are supporting 20 impactful charities through HealthPartners. Your donation will promote wellness and vital programs across Canada.
              </p>
              
              {referrerName && (
                <p className="px-4 py-3 bg-yellow-50 border-l-4 border-yellow-400 mb-4">
                  You were invited by <strong>{referrerName}</strong> to support their walking challenge!
                </p>
              )}
              
              <p className="text-gray-600 mb-8">
                Every contribution helps us reach our goal and makes a significant difference in communities across Canada.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-4">How Your Donation Helps</h4>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Supports wellness programs across Canada</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Funds vital health research initiatives</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Provides resources to 20 impactful charities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Promotes community health and well-being</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-primary bg-opacity-5 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-4">Make a Donation</h4>
                <p className="mb-4">
                  Choose an amount to donate to support our mission.
                </p>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <button 
                    className={`${amount === '25' ? 'bg-primary text-white' : 'bg-white text-primary'} border border-primary hover:bg-primary hover:text-white py-3 rounded-md transition-colors`}
                    onClick={() => handleAmountClick('25')}
                  >$25</button>
                  <button 
                    className={`${amount === '50' ? 'bg-primary text-white' : 'bg-white text-primary'} border border-primary hover:bg-primary hover:text-white py-3 rounded-md transition-colors`}
                    onClick={() => handleAmountClick('50')}
                  >$50</button>
                  <button 
                    className={`${amount === '100' ? 'bg-primary text-white' : 'bg-white text-primary'} border border-primary hover:bg-primary hover:text-white py-3 rounded-md transition-colors`}
                    onClick={() => handleAmountClick('100')}
                  >$100</button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Amount
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 text-center">
                   Note: You will need to confirm or enter the donation amount again on the secure Stripe payment page.
                </p>
                <button 
                  className="w-full bg-secondary hover:bg-opacity-90 text-white font-medium py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                  onClick={handleDonate}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Donate Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Donate; 